import React, { ReactElement, ReactNode } from "react";
import type { NavItem } from "@bellomyinsightstech/app-shell";

type IconProps = { children: ReactNode };

const Icon = ({ children }: IconProps): ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const icons = {
  home: <Icon><path d="m3 11 9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M9 20v-6h6v6" /></Icon>,
  studies: <Icon><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 3v3h6V3" /><path d="M8 11h8M8 15h6" /></Icon>,
  dataExplorer: <Icon><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" /><path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></Icon>,
  crosstabs: <Icon><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 3v18" /></Icon>,
  dashboards: <Icon><rect x="3" y="3" width="7" height="8" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="3" y="15" width="7" height="6" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /></Icon>,
  scheduling: <Icon><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /><path d="m9 16 2 2 4-4" /></Icon>,
  communication: <Icon><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" /><path d="M8 9h8M8 13h5" /></Icon>,
  panelChat: <Icon><circle cx="9" cy="8" r="3" /><circle cx="17" cy="8" r="2" /><path d="M3 20a6 6 0 0 1 12 0M14 15a5 5 0 0 1 7 4" /></Icon>,
  panelists: <Icon><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M16 4a3 3 0 0 1 0 6M17 14a5 5 0 0 1 4 5" /></Icon>,
  automation: <Icon><path d="M12 2v3M12 19v3M4.9 4.9 7 7M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1 7 17M17 7l2.1-2.1" /><circle cx="12" cy="12" r="4" /></Icon>,
  tools: <Icon><path d="M14.7 6.3a4 4 0 0 0-5-5L7.5 3.5l3 3 2.2-2.2" /><path d="m9.5 5.5-7 7a2.1 2.1 0 0 0 3 3l7-7" /><path d="m14 14 6 6" /></Icon>,
  settings: <Icon><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" /></Icon>,
};

const baseItems: readonly NavItem[] = [
  { id: "home", label: "Home", href: "/home", icon: icons.home },
  { id: "studies", label: "Studies & Insights", href: "/studies", icon: icons.studies },
  { id: "data-explorer", label: "Data Explorer", href: "/data-explorer", icon: icons.dataExplorer },
  { id: "crosstabs", label: "Crosstabs", href: "/crosstabs", icon: icons.crosstabs },
  { id: "dashboards", label: "Dashboards", href: "/dashboards", icon: icons.dashboards },
  { id: "scheduling", label: "Panelist Finder", href: "/scheduling", icon: icons.scheduling },
  { id: "communication", label: "Communications", href: "/communication", icon: icons.communication },
  { id: "panel-chat", label: "Chat with Panel", href: "/chat-with-panel", icon: icons.panelChat },
  { id: "panelists", label: "Panelists", href: "/panelists", icon: icons.panelists },
  { id: "automation", label: "Automation", href: "/automation", icon: icons.automation },
];

export const shellSettingsIcon = icons.settings;

export const getShellNavigation = (showTools: boolean): readonly NavItem[] =>
  showTools ? [...baseItems, { id: "tools", label: "Tools", href: "/tools", icon: icons.tools }] : baseItems;

export const getCurrentShellItemId = (path: string): string => {
  if (path === "/" || path.startsWith("/home")) return "home";
  if (path.startsWith("/studies") || path.startsWith("/insights")) return "studies";
  if (path.startsWith("/data-explorer")) return "data-explorer";
  if (path.startsWith("/crosstabs")) return "crosstabs";
  if (path.startsWith("/dashboards")) return "dashboards";
  if (path.startsWith("/scheduling")) return "scheduling";
  if (path.startsWith("/communication")) return "communication";
  if (path.startsWith("/chat-with-panel")) return "panel-chat";
  if (path.startsWith("/panelists")) return "panelists";
  if (path.startsWith("/automation")) return "automation";
  if (path.startsWith("/tools")) return "tools";
  return "home";
};
